import "./styles.css";
import { AUTO_LANGUAGE, SUPPORTED_LANGUAGES, SUPPORTED_OUTPUT_LANGUAGES } from "./languages";

type ClientSecretResponse = {
  value?: string;
  client_secret?: {
    value?: string;
    expires_at?: number;
  };
  session?: {
    client_secret?: {
      value?: string;
      expires_at?: number;
    };
  };
  error?: unknown;
};

const loginView = mustElement<HTMLElement>("login-view");
const appView = mustElement<HTMLElement>("app-view");
const loginForm = mustElement<HTMLFormElement>("login-form");
const passwordInput = mustElement<HTMLInputElement>("password");
const loginError = mustElement<HTMLElement>("login-error");
const appError = mustElement<HTMLElement>("app-error");
const inputLanguage = mustElement<HTMLSelectElement>("input-language");
const outputLanguage = mustElement<HTMLSelectElement>("output-language");
const swapButton = mustElement<HTMLButtonElement>("swap-button");
const startButton = mustElement<HTMLButtonElement>("start-button");
const stopButton = mustElement<HTMLButtonElement>("stop-button");
const logoutButton = mustElement<HTMLButtonElement>("logout-button");
const translatedAudio = mustElement<HTMLAudioElement>("translated-audio");
const statusDot = mustElement<HTMLElement>("status-dot");
const statusText = mustElement<HTMLElement>("status-text");
const connectionState = mustElement<HTMLElement>("connection-state");
const micState = mustElement<HTMLElement>("mic-state");
const latencyState = mustElement<HTMLElement>("latency-state");
const sourceTranscript = mustElement<HTMLElement>("source-transcript");
const targetTranscript = mustElement<HTMLElement>("target-transcript");

let peerConnection: RTCPeerConnection | null = null;
let localStream: MediaStream | null = null;
let dataChannel: RTCDataChannel | null = null;
let startTime = 0;
let heardFirstAudio = false;

fillLanguageMenus();
void boot();

loginForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void login();
});

logoutButton.addEventListener("click", () => {
  void stop();
  void fetch("/api/logout", { method: "POST" }).finally(() => showLogin());
});

swapButton.addEventListener("click", () => {
  if (inputLanguage.value === AUTO_LANGUAGE) {
    return;
  }

  const nextInput = outputLanguage.value;
  const nextOutput = inputLanguage.value;
  inputLanguage.value = nextInput;
  outputLanguage.value = nextOutput;
});

startButton.addEventListener("click", () => {
  void start();
});

stopButton.addEventListener("click", () => {
  void stop();
});

async function boot(): Promise<void> {
  try {
    const response = await fetch("/api/me", { cache: "no-store" });
    const body = (await response.json()) as { authenticated?: boolean };
    if (body.authenticated) {
      showApp();
    } else {
      showLogin();
    }
  } catch (error) {
    showLogin();
    loginError.textContent = getErrorMessage(error);
  }
}

async function login(): Promise<void> {
  loginError.textContent = "";
  const response = await fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password: passwordInput.value }),
  });

  if (!response.ok) {
    loginError.textContent = await responseText(response);
    return;
  }

  passwordInput.value = "";
  showApp();
}

async function start(): Promise<void> {
  await stop();
  setError("");
  setStatus("connecting", "Connecting");
  sourceTranscript.textContent = "";
  targetTranscript.textContent = "";
  latencyState.textContent = "pending";
  heardFirstAudio = false;
  startTime = performance.now();

  try {
    const secret = await createClientSecret();
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
    micState.textContent = "live";

    peerConnection = new RTCPeerConnection();
    peerConnection.addTrack(localStream.getAudioTracks()[0], localStream);
    peerConnection.addTransceiver("audio", { direction: "recvonly" });

    peerConnection.onconnectionstatechange = () => {
      const state = peerConnection?.connectionState ?? "closed";
      connectionState.textContent = state;
      if (state === "connected") {
        setStatus("live", "Live");
      }
      if (state === "failed" || state === "disconnected") {
        setStatus("error", state);
      }
    };

    peerConnection.ontrack = ({ streams }) => {
      translatedAudio.srcObject = streams[0];
      const track = streams[0]?.getAudioTracks()[0];
      if (track) {
        track.onunmute = () => markFirstAudio();
      }
      void translatedAudio.play().then(markFirstAudio).catch(() => {
        setError("Browser blocked audio playback. Press Stop, then Start again.");
      });
    };

    dataChannel = peerConnection.createDataChannel("oai-events");
    dataChannel.onmessage = ({ data }) => handleRealtimeEvent(data);

    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    const sdpResponse = await fetch("https://api.openai.com/v1/realtime/translations/calls", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${secret}`,
        "Content-Type": "application/sdp",
      },
      body: offer.sdp,
    });

    if (!sdpResponse.ok) {
      throw new Error(await responseText(sdpResponse));
    }

    await peerConnection.setRemoteDescription({
      type: "answer",
      sdp: await sdpResponse.text(),
    });

    startButton.disabled = true;
    stopButton.disabled = false;
  } catch (error) {
    await stop();
    setStatus("error", "Error");
    setError(getErrorMessage(error));
  }
}

async function stop(): Promise<void> {
  dataChannel?.close();
  dataChannel = null;

  peerConnection?.getSenders().forEach((sender) => sender.track?.stop());
  peerConnection?.close();
  peerConnection = null;

  localStream?.getTracks().forEach((track) => track.stop());
  localStream = null;

  translatedAudio.pause();
  translatedAudio.srcObject = null;
  startButton.disabled = false;
  stopButton.disabled = true;
  connectionState.textContent = "not connected";
  micState.textContent = "off";

  if (statusText.textContent !== "Error") {
    setStatus("idle", "Idle");
  }
}

async function createClientSecret(): Promise<string> {
  const response = await fetch("/api/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputLanguage: inputLanguage.value,
      outputLanguage: outputLanguage.value,
    }),
  });
  const body = (await response.json()) as ClientSecretResponse;

  if (!response.ok) {
    throw new Error(formatApiError(body) || "Unable to create realtime session");
  }

  const secret = body.value || body.client_secret?.value || body.session?.client_secret?.value;
  if (!secret) {
    throw new Error("Realtime session response did not include a client secret");
  }

  return secret;
}

function handleRealtimeEvent(raw: string): void {
  let event: { type?: string; delta?: string; error?: unknown };
  try {
    event = JSON.parse(raw) as { type?: string; delta?: string; error?: unknown };
  } catch {
    return;
  }

  if (event.type === "session.output_transcript.delta" && event.delta) {
    targetTranscript.textContent += event.delta;
    markFirstAudio();
  }

  if (event.type === "session.input_transcript.delta" && event.delta) {
    sourceTranscript.textContent += event.delta;
  }

  if (event.type === "error") {
    setError(formatApiError(event) || "Realtime API error");
  }
}

function markFirstAudio(): void {
  if (heardFirstAudio || startTime === 0) {
    return;
  }
  heardFirstAudio = true;
  latencyState.textContent = `${Math.round(performance.now() - startTime)} ms`;
}

function fillLanguageMenus(): void {
  addOption(inputLanguage, "Auto detect", AUTO_LANGUAGE);
  for (const language of SUPPORTED_LANGUAGES) {
    addOption(inputLanguage, language.name, language.code);
  }

  for (const language of SUPPORTED_OUTPUT_LANGUAGES) {
    addOption(outputLanguage, language.name, language.code);
  }

  inputLanguage.value = "en";
  outputLanguage.value = "es";
}

function addOption(select: HTMLSelectElement, label: string, value: string): void {
  const option = document.createElement("option");
  option.text = label;
  option.value = value;
  select.options.add(option);
}

function showLogin(): void {
  loginView.hidden = false;
  appView.hidden = true;
  passwordInput.focus();
}

function showApp(): void {
  loginView.hidden = true;
  appView.hidden = false;
}

function setStatus(kind: "idle" | "connecting" | "live" | "error", text: string): void {
  statusDot.className = `status-dot ${kind}`;
  statusText.textContent = text;
}

function setError(message: string): void {
  appError.textContent = message;
}

function mustElement<T>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Missing element #${id}`);
  }
  return element as unknown as T;
}

async function responseText(response: Response): Promise<string> {
  const text = await response.text();
  if (!text) {
    return `${response.status} ${response.statusText}`;
  }

  try {
    return formatApiError(JSON.parse(text)) || text;
  } catch {
    return text;
  }
}

function formatApiError(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (!value || typeof value !== "object") {
    return "";
  }

  const record = value as Record<string, unknown>;
  if (typeof record.error === "string") {
    return record.error;
  }
  if (record.error && typeof record.error === "object") {
    const error = record.error as Record<string, unknown>;
    if (typeof error.message === "string") {
      return error.message;
    }
  }
  if (typeof record.message === "string") {
    return record.message;
  }

  return "";
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
